import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Download, Eye, FileText, ArrowRight, Building2 } from "lucide-react";
import api, { fileUrl } from "@/lib/apiClient";
import SEO from "@/components/SEO";

function RepCard({ d }) {
  const url = d.storage_path ? fileUrl(d.storage_path) : null;
  return (
    <div className="bg-white border border-npst-line rounded-md p-5 hover:border-npst-gold transition-colors" data-testid="doc-record">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1.5">
            {d.reference_no && <span className="font-mono text-[11px] text-npst-gold" data-testid="doc-ref">{d.reference_no}</span>}
            {d.doc_date && <span className="text-[11px] uppercase tracking-wider text-npst-muted font-semibold">{d.doc_date}</span>}
            {d.language && <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{d.language}</span>}
          </div>
          <Link to={`/documents/${d.slug || d.id}`} className="font-serif text-xl text-npst-ink leading-snug hover:text-npst-navy block">{d.subject || d.title}</Link>
          {d.authority && <div className="text-sm text-npst-muted mt-1"><span className="text-npst-inkSoft">To:</span> {d.authority}</div>}
        </div>
        <FileText size={20} className="text-npst-gold shrink-0 mt-1" />
      </div>
      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-npst-line/60">
        <Link to={`/documents/${d.slug || d.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-npst-navy hover:text-npst-gold" data-testid="view-document-btn"><Eye size={14} /> View</Link>
        {url && <a href={url} download className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-npst-navy hover:text-npst-gold" data-testid="download-document-btn"><Download size={14} /> Download PDF</a>}
        <Link to={`/documents/${d.slug || d.id}`} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-npst-gold">Details <ArrowRight size={13} /></Link>
      </div>
    </div>
  );
}

export default function Documents() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [authority, setAuthority] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/documents?limit=300").then(({ data }) => setItems(data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const authorities = useMemo(() => {
    const set = new Set();
    items.forEach((d) => { if (d.authority && d.authority.trim()) set.add(d.authority.trim()); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return items.filter((d) => {
      if (authority && (d.authority || "").trim() !== authority) return false;
      if (!t) return true;
      return [d.reference_no, d.doc_date, d.subject, d.title, d.authority, d.description]
        .filter(Boolean).join(" ").toLowerCase().includes(t);
    });
  }, [items, q, authority]);

  return (
    <div className="fade-in">
      <SEO route="/documents" title="Document Center — Representations | NPST"
        description="Public repository of official representations submitted by the National Public Service Trust to government departments and authorities. Search by reference number, date, subject or authority."
        canonical={`${window.location.origin}/documents`} />
      <section className="border-b border-npst-line paper-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="label-eyebrow text-npst-gold mb-3">Public Document Center</div>
          <h1 className="font-serif text-4xl sm:text-6xl font-semibold text-npst-ink tracking-tight">Representations</h1>
          <p className="mt-4 text-npst-inkSoft max-w-2xl leading-relaxed">Official representations submitted by the National Public Service Trust to government departments, authorities and statutory bodies — searchable and publicly available.</p>
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row gap-3 mb-6 sticky top-4 z-10">
          <div className="flex items-center gap-3 px-4 border border-npst-line rounded-md bg-white focus-within:border-npst-gold flex-1 shadow-sm">
            <Search size={18} className="text-npst-muted" />
            <input data-testid="doc-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by reference no., date, subject or authority…" className="flex-1 py-3 outline-none bg-transparent" />
            {q && <button onClick={() => setQ("")} className="text-xs text-npst-muted hover:text-npst-navy">Clear</button>}
          </div>
          <div className="relative sm:w-72">
            <Building2 size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-npst-muted pointer-events-none" />
            <select data-testid="authority-filter-select" value={authority} onChange={(e) => setAuthority(e.target.value)}
              className="w-full appearance-none pl-10 pr-8 py-3 border border-npst-line rounded-md bg-white text-sm text-npst-ink outline-none focus:border-npst-gold shadow-sm cursor-pointer">
              <option value="">All Authorities</option>
              {authorities.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <ArrowRight size={14} className="absolute right-4 top-1/2 -translate-y-1/2 rotate-90 text-npst-muted pointer-events-none" />
          </div>
        </div>
        {!loading && <div className="text-xs uppercase tracking-wider text-npst-muted mb-4" data-testid="doc-count">{filtered.length} representation{filtered.length !== 1 ? "s" : ""}</div>}
        {loading ? <div className="py-16 text-center text-npst-muted animate-pulse">Loading…</div>
          : filtered.length === 0 ? <div className="py-16 text-center"><p className="font-serif text-2xl text-npst-ink">No representations found</p><p className="text-npst-muted mt-2 text-sm">Try a different search term.</p></div>
          : <div className="grid gap-4">{filtered.map((d) => <RepCard key={d.id} d={d} />)}</div>}
      </section>
    </div>
  );
}
